import { sendTransaction } from '@wagmi/core'
import { NetworkType } from 'abis/set/sets'
import { DEFAULT_NATIVE_DECIMALS } from 'app-constants'
import type { SuccessPayload } from 'components/widgets/invest-component/storage/invest-states'
import { atomsWithMutation } from 'jotai-tanstack-query'
import { wagmiConfig } from 'services/wallet/chains'
import { arbitrumCurrencies, avalancheCurrencies, formatWeiToAmount } from 'utils'
import { type Address, zeroAddress } from 'viem'
import { type QuoteParams, defaultQuoteParams } from '../zero-ex-requests/quotes/model'
import { quotesQuery } from '../zero-ex-requests/quotes/quotes'
import { type TransactionResult, parseTransactionResult } from './utils'

export type GasMultiplier = {
  [chainId: number]: bigint
}

export type ProductGas = {
  [product: Address]: { [address: Address]: bigint }
}

export const gasMultiplier: GasMultiplier = {
  [NetworkType.Mainnet]: BigInt(10000 + 3000),
  [NetworkType.Avalanche]: BigInt(10000 + 2000),
  [NetworkType.Arbitrum]: BigInt(10000 + 500),
  [NetworkType.Base]: BigInt(10000 + 1500),
}

export const defaultProductGas: ProductGas = {
  [avalancheCurrencies.CAI.address]: {
    [avalancheCurrencies.AVAX.address]: BigInt(305000),
    [avalancheCurrencies.USDC.address]: BigInt(375000),
    [avalancheCurrencies.CAI.address]: BigInt(365000),
  },
  [arbitrumCurrencies.ARTXAI.address]: {
    [arbitrumCurrencies.ETH.address]: BigInt(175000),
    [arbitrumCurrencies.USDC.address]: BigInt(250000),
    [arbitrumCurrencies.USDT.address]: BigInt(250000),
    [arbitrumCurrencies.DAI.address]: BigInt(250000),
    [arbitrumCurrencies.ARB.address]: BigInt(250000),
    [arbitrumCurrencies.ARTXAI.address]: BigInt(250000),
  },
}

type TransactionReturn = ReturnType<
  typeof atomsWithMutation<TransactionResult | undefined, unknown, SuccessPayload, unknown>
>
export type TransactionStatus = TransactionReturn['0']
export type TransactionRequest = TransactionReturn['1']

export const defaultGasMultiplier = BigInt(10000 + 500)

export const [indexV1TransactionResponseAtom, indexV1TransactionRequestAtom] = atomsWithMutation<
  TransactionResult | undefined,
  unknown,
  SuccessPayload,
  unknown
>(() => ({
  mutationKey: ['index-v1-transaction'],
  mutationFn: async (props: SuccessPayload) => {
    console.log('index-v1-transaction')
    const { inputCurrency, account, outputCurrency, sellAmount } = props

    const params: QuoteParams = {
      ...defaultQuoteParams,
      sellCurrency: inputCurrency,
      buyCurrency: outputCurrency,
      sellAmount: sellAmount,
      chainId: inputCurrency.chainId,
      account,
    }
    const quotes = await quotesQuery(params)

    const {
      to: presetTo,
      data: preData,
      buyAmount: preBuyAmount,
      sellAmount: preSellAmount,
    } = quotes

    if (inputCurrency.type === 'native') {
      const adjustedAmount = preSellAmount

      const requestedQuotes = await quotesQuery({
        ...params,
        sellAmount: formatWeiToAmount(adjustedAmount, DEFAULT_NATIVE_DECIMALS),
      })

      const { to, data, buyAmount, gas, value } = requestedQuotes
      const hash = await sendTransaction(wagmiConfig, {
        to,
        account,
        value,
        data,
        gas: gas,
      })
      return parseTransactionResult(hash, props, buyAmount)

      // return parseTransactionResult('0x00123', props, Big(123))
      // INFO: test
    }
    const hash = await sendTransaction(wagmiConfig, {
      to: presetTo,
      account,
      value: inputCurrency.address === zeroAddress ? preSellAmount : BigInt(0),
      data: preData,
    })
    return parseTransactionResult(hash, props, preBuyAmount)
    // return parseTransactionResult('0x00123', props, Big(123))
    // INFO: test
  },
}))
